import React, { useEffect, useCallback, useState, useMemo, memo } from 'react'
import { Sticker, MultiLevelCategory } from '@shopby/shop-sdk'
import { isMobile } from 'react-device-detect'
import TagManager from 'react-gtm-module'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { ProductSearchItem } from '../../@types/product'
import { GTMItems } from '../../@types/dataLayerObject'
import { accessToken } from '../../api/auth'
import '../../css/Product.css'
import { AFWebLinkType, RNPostMessage, sendSetAppsflyerEvent } from '../../js/postMessage'
import { formatNumberWithTruncation, onErrorImg, plusComma, setPrevList, setPrevPage } from '../../js/script'
import LikeBtn from '../btn/LikeBtn'
import { Button } from '../Button'
import { useAppContext } from '../provider/AppProvider'
import { IconStarOn } from '../svg/IconSvg'
// 이미지 경로
const imgURL = '/images'

type PropsType = {
  item: any
  setIsModal: React.Dispatch<React.SetStateAction<boolean>>
  setSelectProductNo: React.Dispatch<React.SetStateAction<string>>
  page?: number
  listItem?: ProductSearchItem[]
  idx: number
  totalCount: number
  promotion_id?: number
  promotion_name?: string
  categoryData?: any
  categoryDepth1?: MultiLevelCategory
  categoryDepth2?: MultiLevelCategory
  categoryDepth3?: MultiLevelCategory
  depthLabel?: any
  defaultKeyword?: string
  setCheck?: React.Dispatch<React.SetStateAction<boolean>>
  setCartIdx: React.Dispatch<React.SetStateAction<number>>
}

type ItemData = {
  location: any
  commonItemData: any
  categoryData: any
  promotion_id: any
  promotion_name: any
  defaultKeyword: any
  totalCount: number
  idx: number
  item: any
  categoryDepth1?: MultiLevelCategory
  categoryDepth2?: MultiLevelCategory
  categoryDepth3?: MultiLevelCategory
  depthLabel?: string
}

const ProductItem = ({
  item,
  setIsModal,
  setSelectProductNo,
  page,
  listItem,
  idx,
  totalCount,
  promotion_id,
  promotion_name,
  categoryData,
  categoryDepth1,
  categoryDepth2,
  categoryDepth3,
  depthLabel,
  defaultKeyword,
  setCartIdx,
}: PropsType) => {
  const { isAppView } = useAppContext()
  const navigate = useNavigate()
  const location = useLocation()

  const [disCountAcc, setDisCountAcc] = useState<number>(0)
  const [image, setImage] = useState<Sticker[]>([])
  const [text, setText] = useState<Sticker[]>([])

  const discountPercentage = useMemo(() => Math.floor((item?.immediateDiscountAmt / item?.salePrice) * 100), [item])
  const discountedPrice = useMemo(() => item?.salePrice - item?.immediateDiscountAmt, [item])
  const discountPrice = useMemo(
    () =>
      item?.salePrice -
        item?.immediateDiscountAmt -
        item?.couponDiscountAmt -
        item?.accumulationInfo?.amount -
        (item?.accumulationUseInfo?.usable ? disCountAcc : 0) <
      0
        ? 0
        : item?.salePrice -
          item?.immediateDiscountAmt -
          item?.couponDiscountAmt -
          item?.accumulationInfo?.amount -
          (item?.accumulationUseInfo?.usable ? disCountAcc : 0),
    [item, disCountAcc]
  )

  const createGtmItem = (event: string, items: GTMItems[], eventInfo?: any) => ({
    dataLayer: {
      event,
      ecommerce:
        event === 'select_item' || event === 'select_promotion' ? { item_list_count: totalCount, items } : undefined,
      event_info: event === 'select_search_item' ? eventInfo : undefined,
    },
  })

  const getCommonItemData = (item: any, idx: number) => ({
    item_id: String(item?.productNo),
    item_name: item?.productName,
    affiliation: '어바웃피싱',
    item_list_price: item?.salePrice,
    item_selling_price: item?.salePrice - item?.immediateDiscountAmt,
    price: item?.salePrice - item?.immediateDiscountAmt,
    item_total_discount: item?.immediateDiscountAmt,
    discount: 0,
    index: idx + 1,
    item_brand: item?.brandName,
  })

  const createItem = (event: string, items: GTMItems[], eventInfo?: any) => createGtmItem(event, items, eventInfo)

  const getGtmClickItem = (itemData: ItemData) => {
    const {
      location,
      commonItemData,
      categoryData,
      promotion_id,
      promotion_name,
      defaultKeyword,
      totalCount,
      idx,
      item,
      categoryDepth1,
      categoryDepth2,
      categoryDepth3,
      depthLabel,
    } = itemData

    if (location.pathname.includes('exhibition')) {
      // FIXME: select_promotion 부분 item_category, item_category2, item_category3 도 categoryDepth1 같이 대체 가능할까요?
      return createItem('select_promotion', [
        {
          ...commonItemData,
          item_category: !categoryData?.depth1Label ? '전체' : categoryData?.depth1Label,
          item_category2: categoryData?.depth2Label,
          item_category3: categoryData?.depth3Label,
          promotion_id: String(promotion_id),
          promotion_name: promotion_name,
        },
      ])
    } else if (location.pathname.includes('searchResult')) {
      return createItem('select_search_item', [], {
        search_term: defaultKeyword,
        search_results_count: totalCount,
        item_name: item?.productName,
        item_id: String(item?.productNo),
        click_index: idx,
      })
    } else {
      return createItem('select_item', [
        {
          ...commonItemData,
          item_category: categoryDepth1?.label,
          item_category2: categoryDepth2?.label,
          item_category3: categoryDepth3?.label,
          item_list_id: String(categoryDepth3?.categoryNo),
          item_list_name: depthLabel,
        },
      ])
    }
  }

  const commonItemData = useMemo(() => getCommonItemData(item, idx), [item, idx])
  const itemData = useMemo(
    () => ({
      location,
      commonItemData,
      categoryData,
      promotion_id,
      promotion_name,
      defaultKeyword,
      totalCount,
      idx,
      item,
      categoryDepth1,
      categoryDepth2,
      categoryDepth3,
      depthLabel,
    }),
    [
      location,
      commonItemData,
      categoryData,
      promotion_id,
      promotion_name,
      defaultKeyword,
      totalCount,
      idx,
      item,
      categoryDepth1,
      categoryDepth2,
      categoryDepth3,
      depthLabel,
    ]
  )
  const gtmClickItem = useMemo(() => getGtmClickItem(itemData), [itemData])

  const navigateToProductPage = (item: any, gtmItems: GTMItems[]) => {
    if (isAppView) {
      RNPostMessage(AFWebLinkType.moveToLink, {
        url: `aboutfishing://aboutfishing.com/?page=webview&url=${process.env.REACT_APP_SHOP_URL}/product/${
          item?.productNo
        }?items=${JSON.stringify(gtmItems)}`,
      })
    } else {
      navigate(`/product/${String(item?.productNo)}`, {
        state: { items: gtmItems },
      })
    }
  }

  const updatePrevState = (listItem: any, page: number | undefined) => {
    setPrevList(listItem)
    if (page) setPrevPage(page)
  }

  const linkClickHandler = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
      e.preventDefault()
      const gtmItems: Array<GTMItems> = []

      if (gtmClickItem?.dataLayer?.ecommerce?.items) {
        gtmItems.push(...gtmClickItem.dataLayer.ecommerce.items)
      }

      const eventData = {
        eventName: gtmClickItem?.dataLayer?.event,
        eventType: gtmClickItem?.dataLayer?.ecommerce || gtmClickItem?.dataLayer?.event_info,
      }

      if (isAppView) {
        sendSetAppsflyerEvent(eventData)
      } else {
        TagManager.dataLayer(gtmClickItem)
      }

      navigateToProductPage(item, gtmItems)
      updatePrevState(listItem, page)
    },
    [gtmClickItem, isAppView, navigate, item, listItem, page]
  )

  //카테고리 지정
  // useEffect(() => {
  //   if (location.pathname.includes('category')) {
  //     if (depthLabel === '전체') {
  //       setCategoryDepth3(categoryDepth2?.categoryNo);
  //     } else {
  //       const foundCategory = categoryDepth2?.children?.find((el: any) => el.label === depthLabel);
  //       if (foundCategory) {
  //         setCategoryDepth3(foundCategory.categoryNo);
  //       }
  //     }
  //   }
  // }, [location.pathname, categoryDepth2, depthLabel]);

  useEffect(() => {
    if (item) {
      // 이미지와 텍스트 초기화
      const images = item.stickerInfos?.filter((element: Sticker) => element.type === 'IMAGE') || []
      const texts = item.stickerInfos?.filter((element: Sticker) => element.type !== 'IMAGE') || []

      setImage(images)
      setText(texts)
      // 할인 금액 설정
      const count = formatNumberWithTruncation((Number(item?.salePrice) - Number(item?.immediateDiscountAmt)) * 0.1)
      setDisCountAcc(Number(count.replaceAll(',', '')))
    } else {
      setImage([])
      setText([])
    }
  }, [item])

  return (
    // 상품내역 wrap
    <div className='product_item_wrap'>
      {/* 상품이미지 영역 */}
      <div className='product_image_area item_img'>
        {item.isSoldOut && (
          <div className='soldout'>
            <img src={`${imgURL}/img_soldout.png`} alt='' />
          </div>
        )}
        <LikeBtn
          item={item}
          categoryDepth1={categoryDepth1}
          categoryDepth2={categoryDepth2}
          categoryDepth3={categoryDepth3}
          depthLabel={depthLabel}
          idx={idx}
        />
        {image.map((element: any, index: number) => (
          <div className='item_flag' key={index}>
            <img src={element.label} alt='' />
          </div>
        ))}
        <div onClick={linkClickHandler}>
          <div className='dim'></div>
          <img src={item?.listImageUrls[0]} alt='' onError={onErrorImg} />
        </div>
      </div>

      {/* 장바구니 버튼 */}
      <BtnCart
        className='btn_cart'
        style={{ width: '100%' }}
        onClick={() => {
          setIsModal(true)
          setSelectProductNo(item.productNo)
          if (!item.isSoldOut) setCartIdx(idx)
        }}
      >
        {item.isSoldOut ? (
          '재입고 알림'
        ) : (
          <>
            <img src={`${imgURL}/icon_cart.png`} alt='' /> 장바구니
          </>
        )}
      </BtnCart>

      <div className='product_item' onClick={linkClickHandler}>
        {/* 브랜드명 */}
        {item?.brandName && <div className='product_brand_title'>{item?.brandName}</div>}

        {/* 상품명 */}
        <div className='product_title'>{item?.productName}</div>

        {/* 원 상품가 */}
        {discountPercentage === 0 ? (
          <div className='discounted_price'>
            {/* 상품 할인 적용가 */}
            <p>{plusComma(discountedPrice)}원</p>
          </div>
        ) : (
          <>
            <div className='product_sale_price'>{plusComma(item?.salePrice)}원</div>
            <div className='discounted_price'>
              <p className='percent'>{discountPercentage}%</p>
              {/* 상품 할인 적용가 */}
              <p>{plusComma(discountedPrice)}원</p>
            </div>
          </>
        )}

        {/* 최대혜택가 */}
        {(item.enableCoupons || item?.accumulationUseInfo?.usable) &&
          discountPrice !== item.salePrice - item.immediateDiscountAmt && (
            <div className='max_benefit'>
              <p className='badge'>회원혜택가</p>
              <p>{plusComma(discountPrice)} 원</p>
            </div>
          )}

        {/* 페이백 적립금 5% + 추가적립금 */}
        {accessToken && item?.accumulationInfo?.amount !== 0 && (
          <div className='save_mileage'>
            <p>
              {`페이백 적립금 ${item?.accumulationInfo.rewardRateOfProduct}% `}
              {plusComma(item?.accumulationInfo?.amount)}
              <img src={`${imgURL}/icon_point.png`} alt='' />
            </p>
          </div>
        )}

        {/* 별점 */}
        <div className='star_rating'>
          <IconStarOn width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />
          <span className='review_rating'>{item?.reviewRating}</span>
          <span className='review_num'>({item?.totalReviewCount})</span>

          {/* Npay */}
          <div className='npay'>
            <img src={`${imgURL}/icon_npay.png`} alt='Naver Pay' />
          </div>
        </div>

        {/* 당일배송, 오늘만 특가 */}
        {text.length > 0 && (
          <div className='item_txt_flag_detail'>
            {text.map((el: any, index: number) => (
              <span key={index} className='delivery'>
                {el?.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default memo(ProductItem)

// 각 상품 wrap
export const ProductItemWrap = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
  width: 100%;
`
// 상품 브랜드명
export const ProductBrandTitile = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 14px;
  margin-bottom: 4px;
`
// 상품명
export const ProductTitle = styled.div`
  width: 100%;
  overflow: hidden;
  margin-bottom: 8px;
  font-weight: 500;
  line-height: 18px;
`
// 장바구니 버튼
export const BtnCart = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 7px;
  margin-bottom: 10px;
  color: var(--colBlack);
  border-radius: 4px;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  border: 1px #e9e9e9 solid;
  &.btn_soldout {
    color: var(--colGray4);
  }
  & img {
    width: 20px;
    height: 20px;
  }
  @media screen and (min-width: 768px) {
    margin-bottom: 12px;
  }
`

// 상품 판매가
export const ProductSalePrice = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-decoration-line: line-through;
  color: var(--colGray5);
  @media screen and (min-width: 768px) {
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
  }
`

// 상품 할인 퍼센트, 가격
export const DiscountedPrice = styled.div`
  display: flex;
  gap: 4px;

  & p {
    display: flex;
    font-size: 15px;
    font-weight: 600;
    line-height: 18px;
  }
  & p.percent {
    color: var(--colPointOrange);
  }
`

// 상품 최대혜택가
export const MaximumBenefit = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
  @media screen and (min-width: 768px) {
    margin-bottom: 10px;
  }
  /* 최대혜택가 */
  & p {
    display: flex;
    justify-content: center;
    font-size: 15px;
    font-weight: 600;
    line-height: 16px;
    color: var(--colPointOrange);
    @media screen and (min-width: 768px) {
      font-size: 18px;
      line-height: 22px;
    }
  }
  /* 최대혜택가 badge */
  & p.badge {
    background: var(--colPointOrange);
    width: 56px;
    padding: 3px 6px;
    background: var(--colPointOrange);
    border-radius: 9px;
    font-size: 10px;
    color: var(--colWhite);
    line-height: 11px;
    @media screen and (min-width: 768px) {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 64px;
      height: 22px;
      border-radius: 22px;
      padding: 0 6px;
      font-size: 12px;
    }
  }
`
// 별점, 리뷰
export const StarRating = styled.div`
  display: flex;
  align-items: center;

  /* 별점 */
  & p {
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
  }
  & p:first-child {
    margin-right: 2px;
  }
  /* 리뷰 */
  & p.review_num {
    font-size: 13px;
    font-weight: 500;
    color: var(--colGray5);
    margin-left: 4px;
  }
`
